/**
 * Pure-function LRU eviction policy for the encrypted thumbnail cache.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/AttachmentThumbnailPolicy.swift`.
 *
 * The store calls `shouldEvict(entries, totalBytes)` whenever a new entry
 * would push the cache over the 200 KB byte cap. The function returns the
 * IDs of entries to delete (oldest-accessed first) until the projected total
 * fits within the limit.
 *
 * This module is pure logic — no IndexedDB, no SubtleCrypto. All functions
 * are synchronous and testable without a browser environment.
 *
 * @module AttachmentThumbnailPolicy
 */

import type { ThumbnailDimensionBucket } from "./AttachmentThumbnail";

// ── Constants ─────────────────────────────────────────────────────────────────

/** Maximum byte budget for the thumbnail cache. 200 KB. */
export const THUMB_CACHE_BYTE_LIMIT = 200 * 1024; // 200 KB

// ── Entry type ────────────────────────────────────────────────────────────────

/**
 * A cached entry as seen by the policy layer. The policy only needs
 * the key triple, the byte size, and the last-accessed timestamp for
 * LRU ordering.
 */
export interface ThumbnailCacheEntry {
  readonly attachmentId: string;
  readonly bucket: ThumbnailDimensionBucket;
  /** Byte length of the sealed ciphertext blob. */
  readonly byteLength: number;
  /** Unix timestamp (ms) of the most recent read or write. */
  readonly lastAccessedAt: number;
}

/**
 * Composite key that uniquely identifies a cached thumbnail.
 */
export interface ThumbnailCacheKey {
  readonly attachmentId: string;
  readonly bucket: ThumbnailDimensionBucket;
}

// ── shouldEvict ───────────────────────────────────────────────────────────────

/**
 * Determine which cache entries to evict so the total cache size fits within
 * `limit` bytes after adding `incomingByteLength` new bytes.
 *
 * Strategy: LRU — evict the entry with the oldest `lastAccessedAt` first.
 * Ties are broken by `attachmentId` then `bucket` for deterministic output.
 *
 * Pure function: does not mutate `entries`; returns only the keys to delete.
 *
 * @param entries             Current cache entries.
 * @param incomingByteLength  Size of the entry about to be added. Pass 0
 *                            when you only want to know which entries are
 *                            already over-budget.
 * @param limit               Byte cap. Defaults to `THUMB_CACHE_BYTE_LIMIT`.
 * @returns                   Keys of entries to evict (may be empty).
 */
export function shouldEvict(
  entries: readonly ThumbnailCacheEntry[],
  incomingByteLength: number,
  limit: number = THUMB_CACHE_BYTE_LIMIT,
): ThumbnailCacheKey[] {
  const totalCurrent = entries.reduce((sum, e) => sum + e.byteLength, 0);
  let projected = totalCurrent + incomingByteLength;

  if (projected <= limit) return [];

  // Sort by lastAccessedAt ascending (oldest first). Ties: deterministic order.
  const sorted = [...entries].sort((a, b) => {
    if (a.lastAccessedAt !== b.lastAccessedAt) {
      return a.lastAccessedAt - b.lastAccessedAt;
    }
    if (a.attachmentId !== b.attachmentId) {
      return a.attachmentId < b.attachmentId ? -1 : 1;
    }
    return a.bucket < b.bucket ? -1 : 1;
  });

  const toEvict: ThumbnailCacheKey[] = [];
  for (const entry of sorted) {
    if (projected <= limit) break;
    toEvict.push({ attachmentId: entry.attachmentId, bucket: entry.bucket });
    projected -= entry.byteLength;
  }
  return toEvict;
}

// ── totalBytes ────────────────────────────────────────────────────────────────

/**
 * Sum the byte lengths of all entries. Convenience for store implementations.
 */
export function totalBytes(entries: readonly ThumbnailCacheEntry[]): number {
  return entries.reduce((sum, e) => sum + e.byteLength, 0);
}
